// src/features/TechnicianAnalysis/PartsManager.js
import React, { useState, useEffect } from 'react';
import {
  Box, Card, CardContent, Typography, Grid, Button, Dialog,
  DialogTitle, DialogContent, DialogActions, TextField, Stack,
  FormControl, InputLabel, Select, MenuItem, Chip, IconButton,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, Alert, Tab, Tabs, Switch, FormControlLabel
} from '@mui/material';
import {
  Add, PhotoCamera, Search, LocalShipping, Inventory,
  BuildCircle, Description, Category, Business
} from '@mui/icons-material';
import { useI18n } from '../../i18n/i18n';
import { PartsInventoryService } from '../../services/partsInventoryService';

const PartsManager = ({ user }) => {
  const { t } = useI18n();
  const [inventoryService] = useState(new PartsInventoryService());
  const [parts, setParts] = useState([]);
  const [tabIndex, setTabIndex] = useState(0);
  const [addPartDialog, setAddPartDialog] = useState({ open: false });
  const [receiveDialog, setReceiveDialog] = useState({ open: false, part: null });
  const [searchTerm, setSearchTerm] = useState('');
  const [newPart, setNewPart] = useState({
    name: '',
    category: '',
    model: '',
    description: '',
    supplier: '',
    cost: '',
    minStock: '',
    maxStock: '',
    critical: false,
    compatibleMachines: [],
    specifications: '',
    imageUrl: ''
  });

  useEffect(() => {
    loadParts();
  }, []);

  const loadParts = () => {
    setParts(inventoryService.parts);
  };

  const handleAddPart = async () => {
    const partData = {
      id: `part_${Date.now()}`,
      ...newPart,
      currentStock: 0,
      cost: parseFloat(newPart.cost) || 0,
      minStock: parseInt(newPart.minStock) || 1,
      maxStock: parseInt(newPart.maxStock) || 10,
      lastUsed: null,
      createdAt: new Date().toISOString(),
      createdBy: user.username
    };

    inventoryService.parts.push(partData);
    await inventoryService.saveData();
    loadParts();
    setAddPartDialog({ open: false });
    setNewPart({
      name: '', category: '', model: '', description: '', supplier: '',
      cost: '', minStock: '', maxStock: '', critical: false, 
      compatibleMachines: [], specifications: '', imageUrl: ''
    });
  };

  const handleReceivePart = async (partId, quantity, invoiceNumber, supplier) => {
    try {
      await inventoryService.restockPart(partId, parseInt(quantity));
      
      // Додаємо в історію прийому
      inventoryService.usageHistory.push({
        id: `receive_${Date.now()}`,
        partId,
        partName: parts.find(p => p.id === partId)?.name,
        quantity: parseInt(quantity),
        type: 'receive',
        invoiceNumber,
        supplier: supplier || parts.find(p => p.id === partId)?.supplier,
        date: new Date().toISOString(),
        receivedBy: user.username
      });

      await inventoryService.saveData();
      loadParts();
      setReceiveDialog({ open: false, part: null });
    } catch (error) {
      console.error('Error receiving part:', error);
    }
  };

  const categories = ['pumps', 'valves', 'belts', 'filters', 'sensors', 'motors', 'electronics', 'other'];
  const machines = ['L-12', 'L-15', 'S-05', 'S-08', 'T-01', 'T-02', 'Caldera Principal', 'Introductor', 'Hibrida', 'Kannegiesser'];
  const suppliers = ['TechParts Inc.', 'FluidSystems', 'MotionTech', 'PureWater Co.', 'SmartSensors Ltd.', 'Other'];

  const filteredParts = parts.filter(part =>
    part.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    part.model?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    part.category.toLowerCase().includes(searchTerm.toLowerCase()) ||
    part.supplier?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getStockStatus = (part) => {
    if (part.currentStock === 0) return { status: 'out_of_stock', color: 'error' };
    if (part.currentStock <= part.minStock) return { status: 'low_stock', color: 'warning' };
    if (part.currentStock <= part.minStock * 1.5) return { status: 'medium_stock', color: 'info' };
    return { status: 'in_stock', color: 'success' };
  };

  return (
    <Box sx={{ p: 3, maxWidth: 1200, mx: 'auto' }}>
      <Typography variant="h4" fontWeight="bold" gutterBottom>
        {t('parts_manager_title')}
      </Typography>
      <Typography variant="h6" color="text.secondary" sx={{ mb: 4 }}>
        {t('parts_manager_subtitle')}
      </Typography>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 3 }}>
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => setAddPartDialog({ open: true })}
            >
              {t('add_new_part')}
            </Button>
            <TextField
              placeholder={t('search_parts_placeholder')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: <Search sx={{ mr: 1, color: 'text.secondary' }} />
              }}
              sx={{ flexGrow: 1 }}
            />
          </Stack>

          <Tabs value={tabIndex} onChange={(_, val) => setTabIndex(val)}>
            <Tab label={t('all_parts')} />
            <Tab label={t('low_stock')} />
            <Tab label={t('critical_parts')} />
            <Tab label={t('recently_added')} />
          </Tabs>
        </CardContent>
      </Card>

      {/* Таблиця запчастин */}
      <Card>
        <CardContent>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell><strong>{t('part_name')}</strong></TableCell>
                  <TableCell><strong>{t('model')}</strong></TableCell>
                  <TableCell><strong>{t('category')}</strong></TableCell>
                  <TableCell><strong>{t('stock')}</strong></TableCell>
                  <TableCell><strong>{t('supplier')}</strong></TableCell>
                  <TableCell><strong>{t('status')}</strong></TableCell>
                  <TableCell><strong>{t('actions')}</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredParts
                  .filter(part => {
                    if (tabIndex === 1) return part.currentStock <= part.minStock;
                    if (tabIndex === 2) return part.critical;
                    if (tabIndex === 3) {
                      const createdDate = new Date(part.createdAt);
                      const weekAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000);
                      return createdDate > weekAgo;
                    }
                    return true;
                  })
                  .map(part => {
                    const stockStatus = getStockStatus(part);
                    return (
                      <TableRow key={part.id} hover>
                        <TableCell>
                          <Stack direction="row" alignItems="center" spacing={1}>
                            {part.imageUrl ? (
                              <Box
                                component="img"
                                src={part.imageUrl}
                                alt={part.name}
                                sx={{ width: 40, height: 40, borderRadius: 1, objectFit: 'cover' }}
                              />
                            ) : (
                              <Inventory sx={{ color: 'text.secondary' }} />
                            )}
                            <Box>
                              <Typography variant="body2" fontWeight="medium">
                                {part.name}
                              </Typography>
                              {part.description && (
                                <Typography variant="caption" color="text.secondary">
                                  {part.description}
                                </Typography>
                              )}
                            </Box>
                          </Stack>
                        </TableCell>
                        <TableCell>{part.model || '-'}</TableCell>
                        <TableCell>
                          <Chip label={t(part.category)} size="small" />
                        </TableCell>
                        <TableCell>
                          <Stack direction="row" alignItems="center" spacing={1}>
                            <Typography variant="body2">
                              {part.currentStock}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              / {part.maxStock}
                            </Typography>
                            {part.currentStock <= part.minStock && (
                              <Chip label={t('low_stock')} color="error" size="small" />
                            )}
                          </Stack>
                        </TableCell>
                        <TableCell>{part.supplier}</TableCell>
                        <TableCell>
                          <Chip
                            label={t(stockStatus.status)}
                            color={stockStatus.color}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          <Stack direction="row" spacing={1}>
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={() => setReceiveDialog({ open: true, part })}
                            >
                              {t('receive')}
                            </Button>
                            <Button size="small" variant="text">
                              {t('edit')}
                            </Button>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    );
                  })}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Діалог додавання запчастини */}
      <Dialog open={addPartDialog.open} maxWidth="md" fullWidth>
        <DialogTitle>
          <Stack direction="row" alignItems="center" spacing={1}>
            <BuildCircle color="primary" />
            <Typography variant="h6">{t('add_new_part')}</Typography>
          </Stack>
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} md={6}>
              <TextField
                label={t('part_name')}
                value={newPart.name}
                onChange={(e) => setNewPart({ ...newPart, name: e.target.value })}
                fullWidth
                required
                InputProps={{
                  startAdornment: <Inventory sx={{ mr: 1, color: 'text.secondary' }} />
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label={t('model')}
                value={newPart.model}
                onChange={(e) => setNewPart({ ...newPart, model: e.target.value })}
                fullWidth
                placeholder="Наприклад: ABC-123"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>{t('category')}</InputLabel>
                <Select
                  value={newPart.category}
                  label={t('category')}
                  onChange={(e) => setNewPart({ ...newPart, category: e.target.value })}
                  startAdornment={<Category sx={{ mr: 1, color: 'text.secondary' }} />}
                >
                  {categories.map(cat => (
                    <MenuItem key={cat} value={cat}>{t(cat)}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>{t('supplier')}</InputLabel>
                <Select
                  value={newPart.supplier}
                  label={t('supplier')}
                  onChange={(e) => setNewPart({ ...newPart, supplier: e.target.value })}
                  startAdornment={<Business sx={{ mr: 1, color: 'text.secondary' }} />}
                >
                  {suppliers.map(supp => (
                    <MenuItem key={supp} value={supp}>{supp}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                label={t('description')}
                value={newPart.description}
                onChange={(e) => setNewPart({ ...newPart, description: e.target.value })}
                multiline
                rows={2}
                fullWidth
                InputProps={{
                  startAdornment: <Description sx={{ mr: 1, color: 'text.secondary' }} />
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label={t('specifications')}
                value={newPart.specifications}
                onChange={(e) => setNewPart({ ...newPart, specifications: e.target.value })}
                multiline
                rows={2}
                fullWidth
                placeholder="Технічні характеристики, розміри, матеріал..."
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                label={t('cost')}
                type="number"
                value={newPart.cost}
                onChange={(e) => setNewPart({ ...newPart, cost: e.target.value })}
                fullWidth
                InputProps={{ endAdornment: '€' }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                label={t('min_stock')}
                type="number"
                value={newPart.minStock}
                onChange={(e) => setNewPart({ ...newPart, minStock: e.target.value })}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                label={t('max_stock')}
                type="number"
                value={newPart.maxStock}
                onChange={(e) => setNewPart({ ...newPart, maxStock: e.target.value })}
                fullWidth
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={newPart.critical}
                    onChange={(e) => setNewPart({ ...newPart, critical: e.target.checked })}
                  />
                }
                label={t('critical_part')}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label={t('image_url')}
                value={newPart.imageUrl}
                onChange={(e) => setNewPart({ ...newPart, imageUrl: e.target.value })}
                fullWidth
                placeholder="URL зображення запчастини"
                InputProps={{
                  startAdornment: <PhotoCamera sx={{ mr: 1, color: 'text.secondary' }} />
                }}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddPartDialog({ open: false })}>
            {t('cancel')}
          </Button>
          <Button 
            onClick={handleAddPart} 
            variant="contained"
            disabled={!newPart.name || !newPart.category}
          >
            {t('add_part')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Діалог прийому запчастин */}
      <Dialog open={receiveDialog.open} onClose={() => setReceiveDialog({ open: false, part: null })}>
        <DialogTitle>
          <Stack direction="row" alignItems="center" spacing={1}>
            <LocalShipping color="primary" />
            <Typography variant="h6">{t('receive_parts')}</Typography>
          </Stack>
        </DialogTitle>
        <DialogContent>
          <Typography gutterBottom>
            {t('receiving_part')}: <strong>{receiveDialog.part?.name}</strong>
          </Typography>
          <TextField
            label={t('quantity')}
            type="number"
            fullWidth
            sx={{ mt: 2 }}
            onChange={(e) => setReceiveDialog(prev => ({ ...prev, quantity: e.target.value }))}
          />
          <TextField
            label={t('invoice_number')}
            fullWidth
            sx={{ mt: 2 }}
            onChange={(e) => setReceiveDialog(prev => ({ ...prev, invoiceNumber: e.target.value }))}
          />
          <TextField
            label={t('supplier')}
            fullWidth
            sx={{ mt: 2 }}
            defaultValue={receiveDialog.part?.supplier}
            onChange={(e) => setReceiveDialog(prev => ({ ...prev, supplier: e.target.value }))}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReceiveDialog({ open: false, part: null })}>
            {t('cancel')}
          </Button>
          <Button 
            onClick={() => handleReceivePart(
              receiveDialog.part?.id, 
              receiveDialog.quantity,
              receiveDialog.invoiceNumber,
              receiveDialog.supplier
            )} 
            variant="contained"
          >
            {t('confirm_receive')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PartsManager;